import { useState, useEffect } from "react";
import axios from "axios";
import { MapPin, Clock, ExternalLink, Calendar, History } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventRow = ({ event, formatDate }) => {
  const d = formatDate(event.date);
  return (
    <div
      key={event.id}
      data-testid={`event-item-${event.id}`}
      className="group border-b border-white/5 py-6 md:py-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-8 hover:bg-white/[0.02] transition-colors duration-300 px-2"
    >
      <div className="flex items-baseline gap-2 md:w-32 shrink-0">
        <span className="font-heading text-3xl md:text-4xl tracking-tighter text-white/80">{d.day}</span>
        <div>
          <span className="font-sub text-[10px] text-white/40 tracking-widest block">{d.month}</span>
          <span className="font-sub text-[10px] text-white/20 tracking-widest">{d.year}</span>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-heading text-lg md:text-xl tracking-tight text-white group-hover:text-white/90 truncate">
          {event.title}
        </h3>
        <div className="flex flex-wrap items-center gap-4 mt-2">
          <span className="font-sub text-[10px] text-white/40 tracking-widest inline-flex items-center gap-1.5">
            <MapPin className="w-3 h-3" />
            {event.venue}
          </span>
          <span className="font-sub text-[10px] text-white/30 tracking-widest">{event.city}</span>
          {event.time && (
            <span className="font-sub text-[10px] text-white/30 tracking-widest inline-flex items-center gap-1.5">
              <Clock className="w-3 h-3" />
              {event.time}
            </span>
          )}
        </div>
      </div>
      {event.ticket_link && (
        <a
          href={event.ticket_link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`event-ticket-${event.id}`}
          className="btn-alchemist shrink-0 border border-white/15 bg-transparent hover:bg-white hover:text-black py-3 px-6 font-sub text-[10px] tracking-widest uppercase inline-flex items-center gap-2 transition-colors duration-300"
        >
          Tickets
          <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  );
};

export const EventsSection = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API}/events`);
        setEvents(res.data);
      } catch (err) {
        console.error("Failed to fetch events:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr + "T00:00:00");
      return {
        day: date.getDate().toString().padStart(2, "0"),
        month: date.toLocaleString("en-US", { month: "short" }).toUpperCase(),
        year: date.getFullYear(),
      };
    } catch {
      return { day: "--", month: "---", year: "----" };
    }
  };

  const today = new Date().toISOString().split("T")[0];
  const upcoming = events
    .filter((e) => e.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date));
  const past = events
    .filter((e) => e.date < today)
    .sort((a, b) => b.date.localeCompare(a.date));

  return (
    <section id="events" data-testid="events-section" className="py-24 md:py-32 tracing-border">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="mb-16">
          <p className="font-sub text-[10px] text-white/30 tracking-[0.3em] mb-4">003</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter">EVENTS</h2>
          <div className="gold-line mt-6" />
        </div>

        {/* Loading */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border border-white/5 p-6 animate-pulse">
                <div className="h-4 bg-white/5 w-1/3 mb-3" />
                <div className="h-3 bg-white/5 w-1/4" />
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Upcoming Events */}
            <div data-testid="upcoming-events" className="mb-20">
              <p className="font-sub text-[10px] text-white/50 tracking-[0.3em] mb-8 flex items-center gap-2">
                <Calendar className="w-3 h-3" />
                UPCOMING
              </p>
              {upcoming.length === 0 ? (
                <div className="border border-white/10 p-16 text-center">
                  <Calendar className="w-8 h-8 text-white/15 mx-auto mb-6" />
                  <p className="font-sub text-xs text-white/30 tracking-widest mb-2">NO UPCOMING EVENTS</p>
                  <p className="font-body text-sm text-white/20">Check back soon for new dates.</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {upcoming.map((event) => (
                    <EventRow key={event.id} event={event} formatDate={formatDate} />
                  ))}
                </div>
              )}
            </div>

            {/* Past Shows */}
            {past.length > 0 && (
              <div data-testid="past-events">
                <p className="font-sub text-[10px] text-white/50 tracking-[0.3em] mb-8 flex items-center gap-2">
                  <History className="w-3 h-3" />
                  PAST SHOWS
                </p>
                <div className="space-y-1 opacity-60">
                  {past.map((event) => (
                    <EventRow key={event.id} event={event} formatDate={formatDate} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};
